const KINDS = new Set(['info', 'success', 'warning', 'error', 'confirm', 'invite', 'reward', 'purchase', 'security', 'connection'])
const PRIORITY = { security: 100, error: 90, confirm: 80, purchase: 75, invite: 70, warning: 60, reward: 55, connection: 50, success: 40, info: 20 }
const MAX_TEXT = 280

export function sanitizeNoticeText(value) {
  return [...String(value ?? '')
    .replace(/\bBearer\s+[A-Za-z0-9._~-]+/gi, 'Bearer [hidden]')
    .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[hidden token]')
    .replace(/([?&](?:token|ticket|secret|code)=)[^&\s]+/gi, '$1[hidden]')
    .replace(/\b(token|ticket|secret|cookie)\s*[:=]\s*[^\s,;]+/gi, '$1=[hidden]')]
    .map(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 ? ' ' : character)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_TEXT)
}

export function focusTrapIndex(index, length, backwards = false) {
  if (!Number.isInteger(length) || length <= 0) return -1
  const current = Number.isInteger(index) ? index : 0
  return backwards ? (current - 1 + length) % length : (current + 1) % length
}

export class NotificationQueue {
  // Таймеры по умолчанию обёрнуты в стрелки: голый setTimeout, вызванный как
  // this.setTimer(...), в браузере падает с Illegal invocation (потерян
  // this=window). Node такой вызов прощает — тесты этого не видят.
  constructor({ maxItems = 24, now = () => Date.now(), setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = id => clearTimeout(id), onChange = () => {} } = {}) {
    this.maxItems = maxItems
    this.now = now
    this.setTimer = setTimer
    this.clearTimer = clearTimer
    this.onChange = onChange
    this.items = []
    this.timers = new Map()
    this.sequence = 0
  }

  enqueue(input = {}) {
    const kind = KINDS.has(input.kind) ? input.kind : 'info'
    const dedupeKey = sanitizeNoticeText(input.dedupeKey || '')
    const existing = dedupeKey && this.items.find(item => item.dedupeKey === dedupeKey)
    if (existing) return existing.id
    const modal = input.modal === true || kind === 'confirm'
    const item = {
      id: `notice-${++this.sequence}`,
      kind,
      tone: input.tone || (kind === 'error' ? 'error' : kind === 'warning' ? 'warning' : ['success', 'reward'].includes(kind) ? 'success' : 'info'),
      priority: Number.isFinite(input.priority) ? input.priority : PRIORITY[kind],
      title: sanitizeNoticeText(input.title || ''),
      message: sanitizeNoticeText(input.message ?? input.text),
      confirmText: sanitizeNoticeText(input.confirmText || 'ПОДТВЕРДИТЬ'),
      cancelText: sanitizeNoticeText(input.cancelText || 'ОТМЕНА'),
      dedupeKey,
      modal,
      safeEnter: input.safeEnter === true,
      dismissible: input.dismissible !== false,
      durable: input.durable === true,
      createdAt: this.now(),
      expiresAt: Number.isFinite(input.expiresAt) ? input.expiresAt : null,
      resolve: typeof input.resolve === 'function' ? input.resolve : null,
    }
    if (!item.title && !item.message) return null
    this.items.push(item)
    this.items.sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt)
    while (this.items.length > this.maxItems) {
      const dropped = this.items.pop()
      this.finish(dropped, false)
    }
    const ttl = item.expiresAt ? item.expiresAt - this.now() : modal || item.durable ? null : (Number.isFinite(input.ttlMs) ? input.ttlMs : 4500)
    if (ttl !== null) {
      if (ttl <= 0) this.dismiss(item.id, 'expired')
      else this.timers.set(item.id, this.setTimer(() => this.dismiss(item.id, 'expired'), ttl))
    }
    this.emit()
    return item.id
  }

  confirm(options = {}) {
    return new Promise(resolve => this.enqueue({ ...options, kind: options.kind || 'confirm', modal: true, resolve, dismissible: options.dismissible !== false }))
  }

  respond(id, accepted) {
    const item = this.remove(id)
    if (!item) return false
    this.finish(item, accepted === true)
    this.emit()
    return true
  }

  dismiss(id, reason = 'dismissed') {
    const item = this.remove(id)
    if (!item) return false
    this.finish(item, false)
    this.emit(reason)
    return true
  }

  remove(id) {
    const index = this.items.findIndex(item => item.id === id)
    if (index < 0) return null
    const [item] = this.items.splice(index, 1)
    const timer = this.timers.get(id)
    if (timer !== undefined) this.clearTimer(timer)
    this.timers.delete(id)
    return item
  }

  finish(item, value) {
    if (!item) return
    const timer = this.timers.get(item.id)
    if (timer !== undefined) this.clearTimer(timer)
    this.timers.delete(item.id)
    item.resolve?.(value)
    item.resolve = null
  }

  retainDurable() {
    const removed = this.items.filter(item => !item.durable)
    this.items = this.items.filter(item => item.durable)
    removed.forEach(item => this.finish(item, false))
    this.emit('reconnect')
  }

  snapshot() {
    const publicItem = item => ({ id: item.id, kind: item.kind, tone: item.tone, title: item.title, message: item.message, confirmText: item.confirmText, cancelText: item.cancelText, priority: item.priority, modal: item.modal, safeEnter: item.safeEnter, dismissible: item.dismissible, durable: item.durable, expiresAt: item.expiresAt })
    return {
      modal: this.items.find(item => item.modal) ? publicItem(this.items.find(item => item.modal)) : null,
      toasts: this.items.filter(item => !item.modal).slice(0, 3).map(publicItem),
      queued: this.items.length,
    }
  }

  emit(reason = 'change') {
    this.onChange(this.snapshot(), reason)
  }

  destroy() {
    for (const timer of this.timers.values()) this.clearTimer(timer)
    this.timers.clear()
    this.items.splice(0).forEach(item => this.finish(item, false))
    this.onChange = () => {}
  }
}
